import { emailIsValid } from "@redotech/email";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import RedoLogo from "@redotech/redo-icons/redo-logo-cutout.svg";
import XCloseIcon from "@redotech/redo-icons/x-close.svg";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { getLocations } from "@redotech/redo-merchant-app-common/client/shopify";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import {
  ReloadTeamContext,
  TeamContext,
} from "@redotech/redo-merchant-app-common/team";
import { getCustomerPortalLoginLink } from "@redotech/redo-model/customer-portal";
import { Provider } from "@redotech/redo-model/order";
import { SPECIAL_LOCATION_ID } from "@redotech/redo-model/return-flow-step-valid";
import { GidType, isGid } from "@redotech/redo-model/shopify/gid-utils";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import marketingImageUrl from "./assets/marketing.png?url";
import * as styles from "./withdrawals-onboarding-modal.module.css";
import { StepContent } from "./withdrawals-onboarding-pages";

export type MerchantType = "new" | "existing";
export type StepId =
  | "intro"
  | "requirements"
  | "how-redo-works"
  | "get-started"
  | "account-setup"
  | "go-live"
  | "setup-go-live"
  | "confirmation";
export type ChargeType = "actual" | "flat";
export type ValidationErrors = Partial<
  Record<
    | "returnAddress"
    | "notificationEmail"
    | "chargeAmount"
    | "fulfillmentEmails",
    string
  >
>;

interface StepMeta {
  id: StepId;
  title: string;
}

export interface FormState {
  chargeShipping: boolean;
  chargeType: ChargeType;
  chargeAmount: string;
  returnAddress: string;
  billingReady: boolean;
  notificationEmail: string;
  policyCoverage: boolean;
  fulfillmentNotify: boolean;
  fulfillmentEmails: string[];
}

export interface WithdrawalsOnboardingAddressOption {
  id: string;
  label: string;
  supportingText?: string;
}

interface WithdrawalsOnboardingModalProps {
  onClose: () => void;
  onComplete: () => void;
}

const SHARED_STEPS: StepMeta[] = [
  { id: "intro", title: "Why it matters" },
  { id: "requirements", title: "What the directive requires" },
  { id: "how-redo-works", title: "How Redo works" },
  { id: "get-started", title: "Get started" },
];

const NEW_STEPS: StepMeta[] = [
  { id: "account-setup", title: "Step 1 - Account setup" },
  { id: "go-live", title: "Step 2 - Go live" },
];

const EXISTING_STEPS: StepMeta[] = [
  { id: "setup-go-live", title: "Set up and go live" },
];

const CONFIRMATION_STEP: StepMeta = {
  id: "confirmation",
  title: "You are all set",
};

const DEFAULT_FORM: FormState = {
  chargeShipping: false,
  chargeType: "actual",
  chargeAmount: "",
  returnAddress: "",
  billingReady: false,
  notificationEmail: "",
  policyCoverage: false,
  fulfillmentNotify: false,
  fulfillmentEmails: [],
};

function splitNotificationEmails(value: string): string[] {
  return Array.from(
    new Set(
      value
        .split(/[,\n]/)
        .map((email) => email.trim())
        .filter(Boolean),
    ),
  );
}

function formatNotificationEmails(emails: string[]): string {
  return emails.join(", ");
}

function invalidEmailMessage(emails: string[]): string | undefined {
  const invalidEmail = emails.find((email) => !emailIsValid(email));
  return invalidEmail ? `Invalid email: ${invalidEmail}` : undefined;
}

function validateFlatShippingAmount(value: string): string | undefined {
  const parsedAmount = value.trim().match(/-?\d+(?:[.,]\d+)?/)?.[0];
  if (!parsedAmount) {
    return "Enter a flat return shipping rate.";
  }
  if (Number(parsedAmount.replace(",", ".")) < 0) {
    return "Flat return shipping rate cannot be negative.";
  }
  return undefined;
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "Failed to save withdrawals onboarding settings";
}

function isShopifyLocationGid(value: string): boolean {
  const parts = value.split("/");
  return (
    isGid(value) &&
    parts[parts.length - 2] === GidType.LOCATION &&
    Boolean(parts[parts.length - 1])
  );
}

function visibleSteps(merchant: MerchantType | null): StepMeta[] {
  if (merchant === "new") {
    return [...SHARED_STEPS, ...NEW_STEPS, CONFIRMATION_STEP];
  }
  if (merchant === "existing") {
    return [...SHARED_STEPS, ...EXISTING_STEPS, CONFIRMATION_STEP];
  }
  return SHARED_STEPS;
}

export const WithdrawalsOnboardingModal = memo(
  function WithdrawalsOnboardingModal({
    onClose,
    onComplete,
  }: WithdrawalsOnboardingModalProps) {
    const client = useRequiredContext(RedoMerchantClientContext);
    const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
    const reloadTeam = useRequiredContext(ReloadTeamContext);
    const routes = useRequiredContext(MerchantRoutesContext);
    const navigate = useNavigate();
    const team = useContext(TeamContext);
    const [merchant, setMerchant] = useState<MerchantType | null>(null);
    const [stepIndex, setStepIndex] = useState(0);
    const [form, setForm] = useState<FormState>(DEFAULT_FORM);
    const [billingChecked, setBillingChecked] = useState(false);
    const [savePending, setSavePending] = useState(false);
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
      {},
    );
    const scrollRef = useRef<HTMLDivElement>(null);

    const settingsLoad = useLoad(
      async (signal) => {
        const settings = await rpcClient.getWithdrawalsOnboardingSettings(
          {},
          { signal },
        );
        setForm((prev) => ({
          ...prev,
          billingReady: settings.settings.billingReady,
          chargeAmount: settings.settings.chargeAmount,
          chargeShipping: settings.settings.chargeShipping,
          chargeType: settings.settings.chargeType,
          fulfillmentEmails:
            settings.settings.thirdPartyFulfillmentCancellationEmails,
          fulfillmentNotify:
            settings.settings.enableThirdPartyFulfillmentCancellationEmail,
          notificationEmail: formatNotificationEmails(
            settings.settings.merchantNotificationEmails,
          ),
          returnAddress: settings.settings.returnAddress ?? prev.returnAddress,
        }));
        return settings;
      },
      [rpcClient],
    );

    const addressOptionsLoad = useLoad(
      async (signal) => {
        if (!team) {
          return [];
        }

        const isShopify = team.platform === Provider.SHOPIFY;
        const [locationsData, addressCollectionsData] = await Promise.all([
          isShopify
            ? getLocations(client, { storeUrl: team.storeUrl, signal })
            : Promise.resolve({ locations: [] }),
          rpcClient.getAddressCollections({ types: ["custom"] }, { signal }),
        ]);

        const options: WithdrawalsOnboardingAddressOption[] = [];
        if (isShopify) {
          options.push(
            {
              id: SPECIAL_LOCATION_ID.ORIGINAL,
              label: "Original location (Shopify fulfillment)",
            },
            {
              id: SPECIAL_LOCATION_ID.SHIPMENT_ORIGIN,
              label: "Original location (Fulfillment address)",
            },
          );
        }

        for (const location of locationsData.locations) {
          options.push({ id: location.id, label: location.name });
        }

        for (const customAddress of addressCollectionsData.addresses.filter(
          (address) => address.type === "custom",
        )) {
          if (!customAddress.address) {
            continue;
          }
          options.push({
            id: customAddress._id.toString(),
            label: customAddress.address.name,
          });
        }

        if (team.settings?.redoManagedReturns?.enabled) {
          options.push({
            id: SPECIAL_LOCATION_ID.REDO_MANAGED,
            label: "Redo Managed",
          });
        }

        return options;
      },
      [client, rpcClient, team],
    );

    const steps = visibleSteps(merchant);
    const safeIndex = Math.min(stepIndex, steps.length - 1);
    const step = steps[safeIndex]!;
    const isConfirmation = step.id === "confirmation";
    // The confirmation step is shown after settings are saved, so the final
    // setup step (the one that triggers the save) is the one before it. This
    // only applies once a merchant type is selected, since the confirmation
    // step is not appended to the shared intro steps.
    const isSaveStep = merchant !== null && safeIndex === steps.length - 2;
    // The confirmation step is a post-completion screen and is excluded from
    // the progress total so the bar reads 100% on the final setup step.
    const progressTotal =
      merchant === null
        ? SHARED_STEPS.length +
          Math.max(NEW_STEPS.length, EXISTING_STEPS.length)
        : steps.length - 1;

    useEffect(() => {
      scrollRef.current?.scrollTo({ top: 0 });
    }, [safeIndex]);

    const update = useHandler((patch: Partial<FormState>) => {
      setForm((prev) => ({ ...prev, ...patch }));
      setValidationErrors((prev) => {
        const next = { ...prev };
        for (const key of Object.keys(patch)) {
          delete next[key as keyof ValidationErrors];
        }
        if (patch.chargeShipping === false || patch.chargeType !== undefined) {
          delete next.chargeAmount;
        }
        if (patch.fulfillmentNotify === false) {
          delete next.fulfillmentEmails;
        }
        return next;
      });
    });

    const selectMerchant = useHandler((value: MerchantType) => {
      setMerchant(value);
      setStepIndex(safeIndex + 1);
    });

    const handleBack = useHandler(() => {
      setStepIndex(Math.max(0, safeIndex - 1));
    });

    const handleRestart = useHandler(() => {
      setMerchant(null);
      setStepIndex(0);
    });

    const handleViewDashboard = useHandler(() => {
      onComplete();
      navigate(routes.getWithdrawalHref());
    });

    const [billingCheckLoad, checkBilling] = useTriggerLoad(async (signal) => {
      const settings = await rpcClient.getWithdrawalsOnboardingSettings(
        {},
        { signal },
      );
      setBillingChecked(true);
      setForm((prev) => ({
        ...prev,
        billingReady: settings.settings.billingReady,
      }));
      if (settings.settings.billingReady) {
        reloadTeam();
      }
      return settings;
    });

    const accountSetupBlocked =
      step.id === "account-setup" &&
      (!form.billingReady || settingsLoad.pending || billingCheckLoad.pending);

    const validateCurrentStep = useHandler((): boolean => {
      const errors: ValidationErrors = {};

      if (step.id === "account-setup") {
        if (merchant === "new" && !form.returnAddress) {
          errors.returnAddress = "Select a return address.";
        }
        const notificationEmailError = invalidEmailMessage(
          splitNotificationEmails(form.notificationEmail),
        );
        if (notificationEmailError) {
          errors.notificationEmail = notificationEmailError;
        }
      }

      if (step.id === "go-live" || step.id === "setup-go-live") {
        if (form.chargeShipping && form.chargeType === "flat") {
          const chargeAmountError = validateFlatShippingAmount(
            form.chargeAmount,
          );
          if (chargeAmountError) {
            errors.chargeAmount = chargeAmountError;
          }
        }
        if (form.fulfillmentNotify) {
          if (form.fulfillmentEmails.length === 0) {
            errors.fulfillmentEmails =
              "Add at least one fulfillment recipient or turn this off.";
          } else {
            const fulfillmentEmailError = invalidEmailMessage(
              form.fulfillmentEmails,
            );
            if (fulfillmentEmailError) {
              errors.fulfillmentEmails = fulfillmentEmailError;
            }
          }
        }
      }

      setValidationErrors(errors);
      return Object.keys(errors).length === 0;
    });

    const handleContinue = useHandler(async () => {
      if (!validateCurrentStep()) {
        return;
      }

      if (accountSetupBlocked) {
        setBillingChecked(true);
        return;
      }

      if (isSaveStep) {
        setSavePending(true);
        try {
          let returnAddress: string | undefined;
          if (merchant === "new") {
            returnAddress = form.returnAddress || undefined;
            if (
              returnAddress &&
              team?.platform === Provider.SHOPIFY &&
              isShopifyLocationGid(returnAddress)
            ) {
              const result = await rpcClient.getOrCreateExternalAddress({
                provider: Provider.SHOPIFY,
                externalId: returnAddress,
              });
              returnAddress = result.addressId;
            }
          }

          await rpcClient.saveWithdrawalsOnboardingSettings({
            chargeAmount: form.chargeAmount,
            chargeShipping: form.chargeShipping,
            chargeType: form.chargeType,
            enableThirdPartyFulfillmentCancellationEmail:
              form.fulfillmentNotify,
            merchantNotificationEmails:
              merchant === "new"
                ? splitNotificationEmails(form.notificationEmail)
                : undefined,
            returnAddress,
            thirdPartyFulfillmentCancellationEmails: form.fulfillmentEmails,
          });
          setStepIndex(safeIndex + 1);
        } catch (error) {
          console.error(
            "Failed to save withdrawals onboarding settings",
            error,
          );
          toast(getErrorMessage(error), { variant: "error" });
        } finally {
          setSavePending(false);
        }
        return;
      }
      setStepIndex(safeIndex + 1);
    });

    const returnAddressOptions = addressOptionsLoad.value ?? [];
    const withdrawalPortalLink = team
      ? getCustomerPortalLoginLink(team, undefined, {
          withdraw: "true",
        }).toString()
      : "[withdrawal portal link]";
    const shippingSettingsHref = team
      ? `/stores/${team._id.toString()}/settings/shipping`
      : "/settings/shipping";
    const billingSettingsHref = team
      ? `/stores/${team._id.toString()}/settings/billing`
      : "/settings/billing";
    const notificationSettingsHref = team
      ? `/stores/${team._id.toString()}/settings/customer-portal`
      : "/settings/customer-portal";
    const withdrawalsFlowSettingsHref = team
      ? `/stores/${team._id.toString()}/settings/customer-portal/flows/withdrawals`
      : "/settings/customer-portal/flows/withdrawals";
    const withdrawalSettingsHref = team
      ? `/stores/${team._id.toString()}/settings/withdrawals`
      : "/settings/withdrawals";
    const portalBuilderHref = team
      ? `/stores/${team._id.toString()}/settings/portal-builder`
      : "/settings/portal-builder";

    return (
      <RedoModal
        className={styles.modal}
        hideFooter
        hideHeader
        isOpen
        mainContentClassName={styles.main}
        modalSize="xl"
        noPadding
        onModalCloseRequested={onClose}
        title="Withdrawals onboarding"
        uiSelector="withdrawals-onboarding-modal"
      >
        <div className={styles.shell}>
          <div className={styles.contentColumn}>
            <header className={styles.header}>
              <div className={styles.brandLockup}>
                <RedoLogo className={styles.brandLogo} />
                <Text fontSize="sm" fontWeight="medium">
                  EU withdrawal onboarding
                </Text>
              </div>
              {!isConfirmation && (
                <ProgressBar current={safeIndex} total={progressTotal} />
              )}
            </header>
            <div className={styles.content} ref={scrollRef}>
              <StepContent
                addressOptionsError={Boolean(addressOptionsLoad.error)}
                billingChecked={billingChecked}
                billingCheckPending={billingCheckLoad.pending}
                billingSettingsHref={billingSettingsHref}
                form={form}
                merchant={merchant}
                notificationSettingsHref={notificationSettingsHref}
                onCheckBilling={checkBilling}
                onSelectMerchant={selectMerchant}
                portalBuilderHref={portalBuilderHref}
                returnAddressOptions={returnAddressOptions}
                shippingSettingsHref={shippingSettingsHref}
                stepId={step.id}
                update={update}
                validationErrors={validationErrors}
                withdrawalPortalLink={withdrawalPortalLink}
                withdrawalSettingsHref={withdrawalSettingsHref}
                withdrawalsFlowSettingsHref={withdrawalsFlowSettingsHref}
              />
            </div>
            <footer className={styles.footer}>
              {isConfirmation ? (
                <>
                  <div>
                    <RedoButton
                      hierarchy="secondary"
                      onClick={handleRestart}
                      size="sm"
                      text="Restart flow"
                    />
                  </div>
                  <div>
                    <RedoButton
                      hierarchy="primary"
                      onClick={handleViewDashboard}
                      size="sm"
                      text="View withdrawal dashboard"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    {safeIndex > 0 && (
                      <RedoButton
                        hierarchy="secondary"
                        onClick={handleBack}
                        size="sm"
                        text="Back"
                      />
                    )}
                  </div>
                  <div>
                    {step.id !== "get-started" && (
                      <RedoButton
                        disabled={savePending || accountSetupBlocked}
                        hierarchy="primary"
                        onClick={handleContinue}
                        size="sm"
                        text={
                          isSaveStep && savePending
                            ? "Saving..."
                            : isSaveStep
                              ? "Finish"
                              : "Continue"
                        }
                      />
                    )}
                  </div>
                </>
              )}
            </footer>
          </div>
          <MarketingPanel onClose={onClose} />
        </div>
      </RedoModal>
    );
  },
);

function ProgressBar({ current, total }: { current: number; total: number }) {
  const pct = total > 0 ? ((current + 1) / total) * 100 : 0;
  return (
    <div className={styles.progressTrack}>
      <div className={styles.progressFill} style={{ width: `${pct}%` }} />
    </div>
  );
}

function MarketingPanel({ onClose }: { onClose: () => void }) {
  return (
    <aside className={styles.marketingPanel}>
      <img alt="" className={styles.marketingImage} src={marketingImageUrl} />
      <button
        aria-label="Close withdrawals onboarding"
        className={styles.closeButton}
        onClick={onClose}
        type="button"
      >
        <XCloseIcon />
      </button>
    </aside>
  );
}
